import type {
  MantineSize,
  TextInputProps,
  TextareaProps} from '@mantine/core';
import {
  TextInput,
  Textarea
} from '@mantine/core';
import React from 'react';

/**
 * Text field wrapper switching between Mantine `TextInput` and `Textarea`
 * based on the `multiline` flag, with an `onEnterCallback` keydown hook.
 * @registry Single-line/multiline text field with error text + helper text.
 *
 * Ported from `library/elements/Textbox`. Prop surface kept identical.
 */
export interface TextboxProps {
  label?: string;
  value?: string;
  name?: string;
  ariaLabel?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  errorText?: string;
  placeholder?: string;
  hidden?: boolean;
  type?: string;
  autoComplete?: boolean;

  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  minLength?: number;
  maxLength?: number;

  helperText?: string;

  size?: MantineSize;

  onBlurCallback?: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onClickCallback?: React.MouseEventHandler<HTMLDivElement>;
  onChangeCallback?: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onEnterCallback?: React.KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;

  props?: TextInputProps | TextareaProps;
}
export const Textbox = React.memo<TextboxProps>(
  ({
    ariaLabel,
    label,
    value,
    name,
    onChangeCallback,
    onClickCallback,
    onBlurCallback,
    onEnterCallback,
    required,
    placeholder,
    disabled,
    error,
    errorText,
    helperText,
    type,
    autoComplete,
    minLength = undefined,
    maxLength = 10000,
    multiline,
    minRows,
    maxRows,
    size,
    props,
  }) => {
    function handleKeyDown(
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void {
      if (e.key == 'Enter') {
        onEnterCallback!(e);
      }
    }

    if (multiline) {
      return (
        <Textarea
          maxLength={maxLength}
          minLength={minLength}
          aria-label={ariaLabel}
          label={label}
          value={value}
          name={name}
          placeholder={placeholder}
          onChange={onChangeCallback}
          disabled={disabled}
          onBlur={onBlurCallback}
          onKeyDown={onEnterCallback && handleKeyDown}
          required={required}
          error={error && errorText}
          description={helperText}
          minRows={minRows}
          maxRows={maxRows}
          autoComplete={autoComplete === true ? 'on' : 'new-password'}
          size={size}
          mb="sm"
          {...(props as TextareaProps)}
        />
      );
    }

    return (
      <TextInput
        size={size}
        maxLength={maxLength}
        aria-label={ariaLabel}
        label={label}
        value={value}
        name={name}
        type={type}
        placeholder={placeholder}
        onChange={onChangeCallback}
        disabled={disabled}
        onClick={onClickCallback}
        onBlur={onBlurCallback}
        onKeyDown={onEnterCallback && handleKeyDown}
        required={required}
        error={error && errorText}
        description={helperText}
        autoComplete={autoComplete === true ? 'on' : 'new-password'}
        mb="sm"
        {...(props as TextInputProps)}
      />
    );
  }
);
